import React, { useState, useEffect } from 'react';
import { 
    Paper, TableRow, TableCell, Checkbox, Chip, Typography, Button,
    Box, FormControl, Select, MenuItem, Autocomplete, TextField, Tooltip, IconButton, FormControlLabel, Stack, Divider
} from '@mui/material';
import { 
    Sort as SortIcon, DoneAll as DoneAllIcon, RemoveDone as RemoveDoneIcon, MailOutline as MailOutlineIcon 
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, addDays } from 'date-fns';
import DataTable from '../DataTable';
import CustomDatePicker from '../CustomDatePicker';
import { formatDateDisplay } from '../../utils/dateUtils';
import ConfirmDialog from '../ConfirmDialog';
import TourOrderDialog from './TourOrderDialog'; // Import Dialog gửi mail của Tour
import * as API from '../../api'; // Đảm bảo import API

// --- HẰNG SỐ ---
const BOOKING_STATUS_LABELS = { 'PENDING': 'Chờ xử lý', 'CONFIRMED': 'Đã xác nhận', 'CANCELLED': 'Đã hủy', 'LOST': 'Deal hỏng', 'CLOSED': 'Hoàn thành' };
const BOOKING_STATUS_COLORS = { 'PENDING': 'warning', 'CONFIRMED': 'success', 'CANCELLED': 'error', 'LOST': 'default', 'CLOSED': 'info' };
const OPS_STATUS_LABELS = { 'OPEN': 'Chờ ĐH', 'CONTACTED': 'Đã liên hệ', 'CONFIRMED': 'NCC Xác nhận', 'OPERATING': 'Đang đi', 'DONE': 'Đã đi' };
const OPS_STATUS_COLORS = { 'OPEN': 'error', 'CONTACTED': 'warning', 'CONFIRMED': 'info', 'OPERATING': 'secondary', 'DONE': 'success' };
const DEFAULT_BOOKING_STATUS = ['PENDING', 'CONFIRMED'];
const DEFAULT_OPS_STATUS = Object.keys(OPS_STATUS_LABELS);

const CustomLabel = ({ title }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title}
    </Typography>
);

const TourOpsTab = ({ bookings, toursList, handleViewBooking, onReload, showToast }) => {
    
    // --- 1. STATE BỘ LỌC (GIAO DIỆN) ---
    const [quickFilterMode, setQuickFilterMode] = useState('CUSTOM');
    const [filterStartDate, setFilterStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [filterEndDate, setFilterEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    const [isAllTime, setIsAllTime] = useState(false);
    const [filterBookingStatus, setFilterBookingStatus] = useState(DEFAULT_BOOKING_STATUS);
    const [filterOpsStatus, setFilterOpsStatus] = useState(DEFAULT_OPS_STATUS);
    const [filterTour, setFilterTour] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');

    // --- 2. STATE ACTIVE FILTER (ÁP DỤNG LỌC) ---
    const [activeFilters, setActiveFilters] = useState({
        startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
        bookingStatus: DEFAULT_BOOKING_STATUS,
        opsStatus: DEFAULT_OPS_STATUS,
        allTime: false,
        sort: 'asc',
        tour: null
    });

    // --- 3. STATE THAO TÁC HÀNG LOẠT & DIALOG ---
    const [selectedIds, setSelectedIds] = useState([]); // Chứa ID của BookingTour
    const [confirmModal, setConfirmModal] = useState({ open: false, title: '', content: '', action: null, confirmColor: 'primary' });
    
    // State cho Lệnh điều Tour
    const [isTourOrderOpen, setIsTourOrderOpen] = useState(false);
    const [selectedBookingsForOrder, setSelectedBookingsForOrder] = useState([]);

    // --- HÀM XỬ LÝ SỰ KIỆN GIAO DIỆN BỘ LỌC ---
    const handleQuickFilterChange = (event) => {
        const mode = event.target.value;
        setQuickFilterMode(mode);
        if (mode === 'CUSTOM') return;
        const today = new Date(); let start, end;
        if (mode === 'TODAY') { start = format(today, 'yyyy-MM-dd'); end = format(today, 'yyyy-MM-dd'); }
        else if (mode === 'TOMORROW') { const tmr = addDays(today, 1); start = format(tmr, 'yyyy-MM-dd'); end = format(tmr, 'yyyy-MM-dd'); }
        else if (mode === 'NEXT_3_DAYS') { start = format(today, 'yyyy-MM-dd'); end = format(addDays(today, 3), 'yyyy-MM-dd'); }
        setFilterStartDate(start); setFilterEndDate(end); setIsAllTime(false);
    };

    const handleApplyFilter = () => {
        setActiveFilters({
            startDate: filterStartDate, endDate: filterEndDate, bookingStatus: filterBookingStatus,
            opsStatus: filterOpsStatus, allTime: isAllTime, sort: sortOrder, tour: filterTour
        });
        setSelectedIds([]); // Xóa các mục đã chọn khi đổi bộ lọc
    };

    const toggleSort = () => {
        const newSort = sortOrder === 'asc' ? 'desc' : 'asc';
        setSortOrder(newSort);
        setActiveFilters(prev => ({ ...prev, sort: newSort }));
    };

    const handleToggleAllBookingStatus = () => {
        if (filterBookingStatus.length === Object.keys(BOOKING_STATUS_LABELS).length) setFilterBookingStatus([]);
        else setFilterBookingStatus(Object.keys(BOOKING_STATUS_LABELS));
    };

    const handleToggleAllOpsStatus = () => {
        if (filterOpsStatus.length === Object.keys(OPS_STATUS_LABELS).length) setFilterOpsStatus([]);
        else setFilterOpsStatus(Object.keys(OPS_STATUS_LABELS));
    };

    const getTourFilterOptions = () => {
        const baseNames = [...new Set(toursList.map(t => t.base_tour_name))].filter(Boolean);
        const groupOptions = baseNames.map(base => ({ label: `[NHÓM] ${base}`, value: base, type: 'BASE', group: 'Lọc theo Nhóm Tour' }));
        const specificOptions = toursList.map(t => ({ label: t.name, value: t.tour_code, type: 'SPECIFIC', group: 'Lọc chi tiết từng Tour' }));
        return [...groupOptions, ...specificOptions];
    };

    // --- LOGIC TRẢI PHẲNG & LỌC DỮ LIỆU ---
    const getFilteredBookingTours = () => {
        let allTours = [];
        bookings.forEach(bk => {
            if (bk.tours && bk.tours.length > 0) {
                bk.tours.forEach(tour => {
                    allTours.push({
                        ...tour,
                        // Kế thừa thông tin từ Booking cha
                        bk_code: bk.booking_code,
                        bk_status: bk.booking_status,
                        customer_detail: bk.customer_detail,
                        booking_category: bk.booking_category
                    });
                });
            }
        });

        return allTours.filter(item => {
            if (!activeFilters.allTime) {
                if (item.start_date < activeFilters.startDate || item.start_date > activeFilters.endDate) return false;
            }
            if (activeFilters.bookingStatus.length > 0 && !activeFilters.bookingStatus.includes(item.bk_status)) return false;
            if (activeFilters.opsStatus.length > 0 && !activeFilters.opsStatus.includes(item.operation_status || 'OPEN')) return false;
            
            if (activeFilters.tour) {
                if (activeFilters.tour.type === 'BASE') {
                    const matchedTourInfo = toursList.find(t => t.tour_code === item.tour);
                    if (!matchedTourInfo || matchedTourInfo.base_tour_name !== activeFilters.tour.value) return false;
                } else {
                    if (item.tour !== activeFilters.tour.value) return false;
                }
            }
            return true;
        }).sort((a, b) => {
            const timeA = new Date(a.start_date).getTime();
            const timeB = new Date(b.start_date).getTime();
            return activeFilters.sort === 'asc' ? timeA - timeB : timeB - timeA;
        });
    };

    const displayData = getFilteredBookingTours();

    // --- THAO TÁC BẢNG & HÀNG LOẠT ---
    const handleSelectRow = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedIds(displayData.map(row => row.id));
        else setSelectedIds([]);
    };

    const closeConfirmModal = () => setConfirmModal({ ...confirmModal, open: false });
    const handleConfirmAction = () => {
        if (confirmModal.action) confirmModal.action();
        closeConfirmModal();
    };

    // Hàm cập nhật trạng thái các Tour đang chọn
    const executeBulkUpdate = async (newStatus) => {
        try {
            const updatePromises = selectedIds.map(id => 
                API.updateBookingTourStatus(id, newStatus)
            );
            await Promise.all(updatePromises);

            if (showToast) showToast("Cập nhật trạng thái Tour thành công!", "success");
            setSelectedIds([]); 
            if (onReload) onReload(); // Load lại data tổng
        } catch (error) {
            console.error("Lỗi cập nhật:", error);
            if (showToast) showToast("Có lỗi xảy ra khi cập nhật trạng thái.", "error");
        }
    };

    const handleBulkUpdateStatus = (newStatus) => {
        setConfirmModal({
            open: true,
            title: 'Xác nhận cập nhật',
            content: `Cập nhật trạng thái ${OPS_STATUS_LABELS[newStatus]} cho ${selectedIds.length} dịch vụ Tour đang chọn?`,
            confirmColor: OPS_STATUS_COLORS[newStatus] || 'primary',
            action: () => executeBulkUpdate(newStatus)
        });
    };

    const handleOpenBulkTourOrder = () => {
        // Lấy ra danh sách các CHẶNG TOUR đang được tick chọn
        const selectedToursData = displayData.filter(t => selectedIds.includes(t.id));
        
        // Map thêm array rooms, transports, extras TƯƠNG ỨNG của riêng Tour này từ parent booking
        const enrichedToursData = selectedToursData.map(tour => {
            const parentBk = bookings.find(b => b.booking_code === tour.bk_code) || {};
            return {
                ...tour,
                parent_guests: parentBk.guests || [],
                parent_special_request: parentBk.special_request || '',
                // CHỈ LẤY CÁC DỊCH VỤ CÓ booking_tour TRÙNG VỚI ID CỦA TOUR NÀY
                tour_rooms: (parentBk.rooms || []).filter(r => r.booking_tour === tour.id),
                tour_transports: (parentBk.transports || []).filter(t => t.booking_tour === tour.id),
                tour_extras: (parentBk.extras || []).filter(e => e.booking_tour === tour.id)
            };
        });
        
        setSelectedBookingsForOrder(enrichedToursData);
        setIsTourOrderOpen(true);
    };

    return (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            
            {/* === KHU VỰC BỘ LỌC TƯƠNG TỰ DASHBOARD CŨ === */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%', mb: 2 }}>
                    <FormControl size="small" sx={{ width: '160px', pt: 3.2 }}>
                        <Select value={quickFilterMode} onChange={handleQuickFilterChange} displayEmpty>
                            <MenuItem value="CUSTOM">-- Lọc nhanh --</MenuItem>
                            <MenuItem value="TODAY">Hôm nay</MenuItem>
                            <MenuItem value="TOMORROW">Ngày mai</MenuItem>
                            <MenuItem value="NEXT_3_DAYS">3 ngày tới</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box style={{ width: '160px' }}>
                            <CustomLabel title="Từ ngày đi" />
                            <CustomDatePicker value={filterStartDate} disabled={isAllTime || quickFilterMode !== 'CUSTOM'} onChange={(e) => setFilterStartDate(e.target.value)} />
                        </Box>
                        <Box style={{ width: '160px' }}>
                            <CustomLabel title="Đến ngày đi" />
                            <CustomDatePicker value={filterEndDate} disabled={isAllTime || quickFilterMode !== 'CUSTOM'} onChange={(e) => setFilterEndDate(e.target.value)} />
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Lọc theo Tour / Nhóm Tour" />
                        <Autocomplete 
                            size="small" fullWidth 
                            options={getTourFilterOptions()} groupBy={(option) => option.group} getOptionLabel={(option) => option.label} 
                            value={filterTour} onChange={(e, newValue) => setFilterTour(newValue)} 
                            renderInput={(params) => <TextField {...params} placeholder="Chọn tour..." />} 
                        />
                    </Box>
                    <Box sx={{ pt: 3.2 }}>
                        <Tooltip title={`Sắp xếp ngày đi: ${sortOrder === 'asc' ? 'Tăng dần' : 'Giảm dần'}`}>
                            <IconButton onClick={toggleSort} sx={{ border: '1px solid #ced4da', borderRadius: 1, height: '40px', color: '#007373', bgcolor: 'white', marginBottom: '12px' }}>
                                <SortIcon sx={{ transform: sortOrder === 'desc' ? 'scaleY(-1)' : 'none' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{ pt: 3.2 }}>
                        <Button variant="contained" onClick={handleApplyFilter} sx={{ bgcolor: '#007373', '&:hover': { bgcolor: '#005c5c' }, height: '40px', px: 3, borderRadius: 2, whiteSpace: 'nowrap', marginBottom: '12px' }}>
                            Áp dụng bộ lọc
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Box>
                        <FormControlLabel 
                            control={<Checkbox checked={isAllTime} onChange={(e) => { setIsAllTime(e.target.checked); if(e.target.checked) setQuickFilterMode('CUSTOM'); }} sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' }, p: 0.5 }} />} 
                            label={<Typography variant="body2" fontWeight="bold">Toàn thời gian</Typography>} 
                            sx={{ m: 0, mt: 2.5 }} 
                        />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: '300px', borderLeft: '1px solid #eee', pl: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <CustomLabel title="Trạng thái BK Gốc" />
                            <Tooltip title={filterBookingStatus.length === Object.keys(BOOKING_STATUS_LABELS).length ? "Bỏ chọn tất cả" : "Chọn tất cả"}>
                                <IconButton size="small" onClick={handleToggleAllBookingStatus} sx={{ padding: 0, color: '#007373' }}>
                                    {filterBookingStatus.length === Object.keys(BOOKING_STATUS_LABELS).length ? <RemoveDoneIcon fontSize="small" /> : <DoneAllIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {Object.keys(BOOKING_STATUS_LABELS).map((key) => (
                                <FormControlLabel
                                    key={key}
                                    control={<Checkbox checked={filterBookingStatus.indexOf(key) > -1} onChange={(e) => setFilterBookingStatus(prev => e.target.checked ? [...prev, key] : prev.filter(k => k !== key))} size="small" sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' }, p: 0.5 }} />}
                                    label={<Typography variant="body2">{BOOKING_STATUS_LABELS[key]}</Typography>}
                                    sx={{ mr: 0, ml: 0 }}
                                />
                            ))}
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: '300px', borderLeft: '1px solid #eee', pl: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <CustomLabel title="Trạng thái ĐH Tour" />
                            <Tooltip title={filterOpsStatus.length === Object.keys(OPS_STATUS_LABELS).length ? "Bỏ chọn tất cả" : "Chọn tất cả"}>
                                <IconButton size="small" onClick={handleToggleAllOpsStatus} sx={{ padding: 0, color: '#007373' }}>
                                    {filterOpsStatus.length === Object.keys(OPS_STATUS_LABELS).length ? <RemoveDoneIcon fontSize="small" /> : <DoneAllIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {Object.keys(OPS_STATUS_LABELS).map((key) => (
                                <FormControlLabel
                                    key={key}
                                    control={<Checkbox checked={filterOpsStatus.indexOf(key) > -1} onChange={(e) => setFilterOpsStatus(prev => e.target.checked ? [...prev, key] : prev.filter(k => k !== key))} size="small" sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' }, p: 0.5 }} />}
                                    label={<Typography variant="body2">{OPS_STATUS_LABELS[key]}</Typography>}
                                    sx={{ mr: 0, ml: 0 }}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* === THANH BULK ACTION TƯƠNG TỰ CŨ === */}
            {selectedIds.length > 0 && (
                <Paper sx={{ p: 1, mb: 2, bgcolor: '#e0f2f1', border: '1px solid #007373', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', ml: 2, color: '#007373' }}>
                        Đang chọn: {selectedIds.length} Tour
                    </Typography>
                    
                    <Button 
                        variant="contained" size="small" onClick={handleOpenBulkTourOrder} startIcon={<MailOutlineIcon />}
                        sx={{ bgcolor: '#007373', '&:hover': { bgcolor: '#005c5c' }, fontWeight: 'bold' }}
                    >
                        Tạo lệnh điều tour / Gửi Mail
                    </Button>

                    <Divider orientation="vertical" flexItem />

                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Cập nhật trạng thái ĐH:</Typography>
                    <Stack direction="row" spacing={1}>
                        {['CONTACTED', 'CONFIRMED', 'OPERATING', 'DONE'].map(status => (
                            <Button 
                                key={status} size="small" variant="contained" 
                                sx={{ 
                                    bgcolor: 'white', color: theme => theme.palette[OPS_STATUS_COLORS[status]].main,
                                    border: '1px solid', borderColor: theme => theme.palette[OPS_STATUS_COLORS[status]].main,
                                    '&:hover': { bgcolor: theme => theme.palette[OPS_STATUS_COLORS[status]].main, color: 'white' }
                                }}
                                onClick={() => handleBulkUpdateStatus(status)} 
                            >
                                {OPS_STATUS_LABELS[status]}
                            </Button>
                        ))}
                    </Stack>
                </Paper>
            )}

            {/* === BẢNG DỮ LIỆU === */}
            <DataTable
                maxHeight="65vh"
                columns={[
                    {
                        label: (
                            <Checkbox color="primary" sx={{ p: 0, color: 'white' }}
                                indeterminate={selectedIds.length > 0 && selectedIds.length < displayData.length}
                                checked={displayData.length > 0 && selectedIds.length === displayData.length}
                                onChange={handleSelectAll}
                            />
                        ),
                        minWidth: 50, align: 'center'
                    },
                    { label: 'Mã BK', minWidth: 100 },
                    { label: 'Khách hàng', minWidth: 180 },
                    { label: 'Tên Dịch vụ Tour', minWidth: 250 },
                    { label: 'Ngày đi', minWidth: 100 },
                    { label: 'NCC Tour', minWidth: 150 },
                    { label: 'Trạng thái ĐH', minWidth: 120 },
                    { label: 'Thao tác', align: 'center', minWidth: 100 },
                ]}
                data={displayData}
                renderRow={(row) => (
                    <TableRow key={row.id} hover>
                        <TableCell padding="checkbox" align="center">
                            <Checkbox color="primary" checked={selectedIds.includes(row.id)} onChange={(e) => { e.stopPropagation(); handleSelectRow(row.id); }} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#007373' }}>{row.bk_code}</TableCell>
                        <TableCell>
                            <Typography variant="body2" fontWeight="bold">{row.customer_detail?.name || 'Khách'}</Typography>
                            <Typography variant="caption">{row.customer_detail?.phone}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" fontWeight="bold">{row.custom_tour_name || 'Tour ghép'}</Typography>
                            <Typography variant="caption" color="textSecondary">{row.duration_days} ngày</Typography>
                        </TableCell>
                        <TableCell>{formatDateDisplay(row.start_date)}</TableCell>
                        <TableCell>{row.supplier_name || 'Chưa gán'}</TableCell>
                        <TableCell>
                            <Chip label={OPS_STATUS_LABELS[row.operation_status || 'OPEN']} color={OPS_STATUS_COLORS[row.operation_status || 'OPEN']} size="small" />
                        </TableCell>
                        <TableCell align="center">
                            <Button size="small" variant="outlined" color="warning" sx={{ fontSize: '0.7rem', py: 0.5, textTransform: 'none', bgcolor: 'white' }} onClick={() => handleViewBooking(row.bk_code)}>
                                Xem BK
                            </Button>
                        </TableCell>
                    </TableRow>
                )}
            />
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#007373' }}>
                    Tổng: {displayData.length} dịch vụ Tour
                </Typography>
            </Box>

            {/* --- CÁC DIALOG KÈM THEO --- */}
            <TourOrderDialog 
                open={isTourOrderOpen}
                onClose={() => setIsTourOrderOpen(false)}
                selectedBookings={selectedBookingsForOrder}
                tours={toursList}
                onViewDetail={handleViewBooking} 
                showToast={showToast}
                onSuccess={() => { setSelectedIds([]); if(onReload) onReload(); }}
            />

            <ConfirmDialog 
                open={confirmModal.open}
                title={confirmModal.title}
                content={confirmModal.content}
                onClose={closeConfirmModal}
                onConfirm={handleConfirmAction}
                confirmColor={confirmModal.confirmColor}
            />
        </Paper>
    );
};
export default TourOpsTab;